import React, { useContext, useEffect, useMemo, useRef } from "react";
import TourLayer from "../globalSearch/TourLayer";
import { startTour } from "../globalSearch/annaBus";
import { ChatbotContext } from "../../context/ChatbotContext";
import { setQuickTourOpen } from "../chatbot/quickTourState";
import AssessmentIcon from "../common/AssessmentIcon";
import Icon from "../common/icons";
import { shouldOfferApp } from "../../pwa/installPrompt";
import { PHONE_QUERY } from "./StudentNav";
import "./StudentTour.css";

// The landing page's "Take a quick tour": the admin guided tour (spotlight, gliding card,
// step dots) pointed at the student's own page. It only opens and closes the chat and the
// account menu; it never writes into the chat or opens the test list, the "Welcome back"
// window or a test (those call the server). While it runs the chat opens as a plain window
// (chatbot/quickTourState.js). What a test looks like is a small preview in the step card.

export const STUDENT_TOUR_ID = "student-quick-tour";

/** Starts the tour: the hero's "Take a quick tour" button, and after a first sign-in. */
export const startStudentTour = () => startTour(STUDENT_TOUR_ID);

// The test list (chatbot/AssessmentPicker.js): one test to start, one already started
const PickerDemo = () => (
  <div className="stour-demo stour-picker">
    <div className="stour-test">
      <span className="stour-tile"><AssessmentIcon type="riasec" size={18} /></span>
      <span className="stour-test-text">
        <b>Career interests</b>
        <small><Icon name="list" size={13} />42 questions</small>
      </span>
      <Icon name="chevron-right" size={16} className="stour-chev" />
    </div>
    <div className="stour-test">
      <span className="stour-tile"><AssessmentIcon type="dass42" size={18} /></span>
      <span className="stour-test-text">
        <b>Mood and stress</b>
        <small className="is-progress"><Icon name="play" size={13} />Continue · question 13 of 42</small>
      </span>
      <Icon name="chevron-right" size={16} className="stour-chev" />
    </div>
  </div>
);

// A question and its answers (chatbot/testShell/TestChat.js), with a pretend tap
const AnswerDemo = () => (
  <div className="stour-demo">
    <div className="stour-q">
      <span className="stour-qnum">4 of 25</span>
      <span>I usually share with others, for example games or food.</span>
    </div>
    <div className="stour-panel-head">
      <span>In the past month, is this true for you?</span>
      <span className="stour-back"><Icon name="undo" size={12} />Back to question 3</span>
    </div>
    <div className="stour-opts">
      <span><kbd>0</kbd>Not true</span>
      <span><kbd>1</kbd>Somewhat true</span>
      <span className="is-picked"><kbd>2</kbd>Certainly true</span>
    </div>
  </div>
);

// The chat's top bar during a test: the name, the question and a line filling up
const ProgressDemo = () => (
  <div className="stour-demo stour-demo--progress">
    <div className="stour-progress-label">Mood and stress <span aria-hidden="true">·</span> 13 of 42</div>
    <div className="stour-progress-track">
      <span />
    </div>
  </div>
);

const buildSteps = ({ openChat, closeChat, termsAccepted, offerApp }) => {
  // Results, Appointments and Profile: the tab bar on phones, the account menu elsewhere
  const showAccountLinks = () => {
    closeChat();
    if (window.matchMedia(PHONE_QUERY).matches) return undefined;
    window.dispatchEvent(new Event("tour:openUserMenu"));
    return () => window.dispatchEvent(new Event("tour:closeUserMenu"));
  };

  const steps = [
    {
      title: "Welcome to Anna",
      body: "Here’s how your tests with Anna work and where your results go. Press Next to begin, or Esc to stop.",
    },
    {
      // Not clickOnNext: the hero button opens the terms dialog when it thinks the chat is
      // already open. The next step opens the chat itself.
      enter: closeChat,
      target: [".lp-hero-actions .lp-btn--primary", ".cl-pill"],
      title: "Start a chat",
      body: "Chat with Anna opens your chat. The yellow button in the corner opens it too, and once you’ve started a test it shows where you stopped.",
    },
    {
      enter: openChat,
      target: ".chatbot",
      wait: 2500,
      title: "Your chat with Anna",
      body: "Anna’s messages and your answers appear here. Close it with the X whenever you need a break.",
    },
    termsAccepted
      ? {
          enter: openChat,
          target: [".chatbot .cb-action.is-choose", ".chatbot .chatbot-messages", ".chatbot"],
          wait: 2500,
          title: "Choose a test",
          body: "This button lists the tests your guidance office gave you. Tap one to see what it’s about, then start it. A test you’ve started says where you stopped.",
          demo: <PickerDemo />,
        }
      : {
          enter: openChat,
          target: [".chatbot .cb-action", ".chatbot .terms-conditions-link", ".chatbot .chatbot-messages"],
          wait: 2500,
          title: "Read the terms first",
          body: "Tap Read the terms, read them and agree. Then a Choose a test button lists the tests your guidance office gave you.",
        },
    {
      enter: openChat,
      target: [".chatbot .chatbot-messages", ".chatbot .ts-view", ".chatbot"],
      wait: 2500,
      title: "One question at a time",
      body: "Each question has its number. Tap an answer at the bottom of the chat, or press its number key. Back to question lets you change the one before.",
      demo: <AnswerDemo />,
    },
    {
      enter: openChat,
      target: [".chatbot .a11y-toggle", ".chatbot .chatbot-header"],
      wait: 2500,
      title: "Make it easier to read",
      body: "This button makes the words and buttons bigger. If Read questions aloud is there too, Anna can say each question out loud.",
    },
    {
      enter: openChat,
      target: [".chatbot .chatbot-header", ".chatbot"],
      wait: 2500,
      title: "Stop and come back",
      body: "During a test, the top of the chat shows how far you are. Every answer is saved, so you can close the chat and finish later. Anna picks up where you stopped.",
      demo: <ProgressDemo />,
    },
    {
      enter: showAccountLinks,
      target: [".sh-tabbar", ".sh-menu", ".sh-account-btn"],
      wait: 2500,
      title: "Your results and appointments",
      body: "Results has the tests you’ve finished. Some show up after your guidance office has looked at them. Appointments is where you book a time to talk with them, and Profile has your details.",
    },
  ];

  // Phones that don't have the app yet: the button under the hero's main buttons. It can sit
  // under the tab bar, which the tour's own scrolling doesn't notice, so bring it up first.
  if (offerApp) {
    steps.push({
      enter: () => {
        closeChat();
        const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
        document.querySelector(".pwa-landing-btn")?.scrollIntoView({ block: "center", behavior: reduce ? "auto" : "smooth" });
      },
      target: ".pwa-landing-btn",
      wait: 1500,
      title: "Keep Anna on your phone",
      body: "Get the ANNA app puts Anna on your home screen, so your tests are one tap away.",
    });
  }
  return steps;
};

const StudentTour = () => {
  const { setShowbot, isAgreeTermsConditions } = useContext(ChatbotContext);
  // Read when the tour starts, so the steps fit where the student is
  const termsRef = useRef(isAgreeTermsConditions);
  termsRef.current = isAgreeTermsConditions;

  // Leaving the page mid-tour mustn't keep the chat quiet
  useEffect(() => () => setQuickTourOpen(false), []);

  const guides = useMemo(() => {
    const openChat = () => setShowbot(true);
    const closeChat = () => setShowbot(false);
    return {
      [STUDENT_TOUR_ID]: {
        title: "Quick tour",
        tour: () => {
          setQuickTourOpen(true);
          return buildSteps({
            openChat,
            closeChat,
            termsAccepted: termsRef.current,
            offerApp: shouldOfferApp() && Boolean(document.querySelector(".pwa-landing-btn")),
          });
        },
        onEnd: () => setQuickTourOpen(false),
        doneLabel: "Start chatting",
        onDone: openChat,
      },
    };
  }, [setShowbot]);

  return <TourLayer guides={guides} blockPage missingNote="This part isn’t on screen right now." />;
};

export default StudentTour;
